import React from 'react';
import PropTypes from 'prop-types';
import { Text } from 'rebass/styled-components';
import { Input } from '@getro/rombo';

const MultiSelectInput = ({
  inputRef,
  onLoseFocus,
  selected,
  handleSearch,
  setShowDropDown,
  setSearch,
  useSearchAsTag,
  onSelect,
  onCloseDropDown,
  search,
  placeholder,
  disableSearch,
  disableSearchText,
  hasError,
  isValidTagValue,
}) => {
  if (disableSearch) {
    return (
      <Text fontSize="14px" color="text.placeholder">
        {disableSearchText}
      </Text>
    );
  }

  return (
    <Input
      ref={inputRef}
      autoFocus
      onBlur={() => {
        if (onLoseFocus) {
          onLoseFocus(selected.map((item) => item.value));
        }
      }}
      data-testid="multi-select-search"
      onChange={({ target }) => {
        setShowDropDown(true);
        handleSearch(target.value);
        setSearch(target.value);
      }}
      onKeyDown={({ target, key }) => {
        if (!useSearchAsTag || key !== 'Enter') return;

        const trimmedValue = target.value.trim();
        if (!trimmedValue) return;

        // isValidTagValue judges the value being committed, so it wins over
        // hasError, which reflects the last render of a 300ms-debounced check
        // and would otherwise swallow the first Enter after a fix.
        const canCreateTag = isValidTagValue ? isValidTagValue(trimmedValue) : !hasError;
        if (!canCreateTag) return;

        onSelect([...selected, { label: trimmedValue, value: trimmedValue }]);
        setSearch('');
        onCloseDropDown();
      }}
      value={search}
      sx={{
        height: '32px',
        fontSize: '14px',
        outline: hasError ? 'none' : 'initial',
        borderColor: hasError ? 'red.500' : 'neutral.400',
      }}
      placeholder={placeholder}
    />
  );
};

MultiSelectInput.propTypes = {
  inputRef: PropTypes.object.isRequired,
  onLoseFocus: PropTypes.func,
  selected: PropTypes.array.isRequired,
  handleSearch: PropTypes.func.isRequired,
  setShowDropDown: PropTypes.func.isRequired,
  setSearch: PropTypes.func.isRequired,
  useSearchAsTag: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onCloseDropDown: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disableSearch: PropTypes.bool,
  disableSearchText: PropTypes.string,
  hasError: PropTypes.bool,
  isValidTagValue: PropTypes.func,
};

MultiSelectInput.defaultProps = {
  placeholder: '',
  disableSearch: true,
  disableSearchText: '',
  onLoseFocus: true,
  useSearchAsTag: false,
  hasError: false,
  isValidTagValue: null,
};

export default MultiSelectInput;
